import axios from 'axios'
import wrapCall, { WrappedResponse } from './wrapCall'
import { stringify } from 'query-string'
import { RECORD_STATUS } from '@qflow/constants'

import type { PagedResponse, WasteCarrier, WasteFacility } from './API.types'

export type GetUri = (uri: string) => string
export type GetHeaders = () => Record<string, string>

export class API {
  _getUri: GetUri
  _getHeaders: GetHeaders
  static abortController: AbortController = new AbortController()

  constructor(getUri: GetUri, getHeaders: GetHeaders) {
    this._getUri = getUri
    this._getHeaders = getHeaders
  }

  static Abort() {
    this.abortController.abort()
    this.abortController = new AbortController()
  }

  static DEFAULT_STATUS_OPTIONS = [
    RECORD_STATUS.FAILED,
    RECORD_STATUS.VOID,
    RECORD_STATUS.PUBLISHED,
    RECORD_STATUS.ESCALATED,
    RECORD_STATUS.AUTOPUBLISHED
  ]

  //
  // Accounts

  getAccount = async (accountId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/accounts/${accountId}`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  listAccounts = async () => {
    return await wrapCall(
      axios.get<any[]>(this._getUri('/accounts'), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  adminListAccounts = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/admin/accounts'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  registerAccount = async (name: string) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri('/accounts/register'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 201,
        data: {
          name
        }
      })
    )
  }

  updateAccount = async (account: { accountId: string; name: string }) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`/accounts/${account.accountId}`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: {
          name: account.name
        }
      })
    )
  }

  //
  // Projects

  getProject = async (accountId: string, projectId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/accounts/${accountId}/projects/${projectId}`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  listProjects = async (accountId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/accounts/${accountId}/projects`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  registerProject = async (accountId: string, name: string) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(`/accounts/${accountId}/projects/register`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 201,
        data: {
          name
        }
      })
    )
  }

  getProductGroups = async (accountId: string, projectId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/delivery/admin/records/productgroups`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getReferenceProductGroups = async (accountId: string, projectId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/productgroups`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  setProductGroups = async (
    accountId: string,
    projectId: string,
    productGroups: any[]
  ) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/productgroups`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: productGroups
      })
    )
  }

  updateProductMemberships = async (
    accountId: string,
    projectId: string,
    projectGroups: any[]
  ) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/productmemberships`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 201,
        data: projectGroups
      })
    )
  }

  updateProject = async (
    accountId: string,
    projectId: string,
    project: {
      name: string
      moduleTags: { moduleTagDefinitionId: string; isTagged: boolean }
      timeZone: string
    }
  ) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`/accounts/${accountId}/projects/${projectId}`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: project
      })
    )
  }

  closeOutProject = async (accountId: string, projectId: string) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/closeout`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 204
      })
    )
  }

  updateProjectProcore = async (
    accountId: string,
    projectId: string,
    procore: {
      companyId: string
      projectId: string
    }
  ) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/procoreConfiguration`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 204,
        data: procore
      })
    )
  }

  deleteProjectProcore = async (accountId: string, projectId: string) => {
    return await wrapCall(
      axios({
        method: 'DELETE',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/procoreConfiguration`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 204
      })
    )
  }

  //
  // Project Modules

  enableTags = async (
    accountId: string,
    projectId: string,
    tagIds: string[] = []
  ) => {
    const [projects, status, error] = await this.listProjects(accountId)
    if (error) {
      return Promise.resolve([null, status, error])
    }

    const project = projects.find(
      (project: { projectId: string }) => project.projectId === projectId
    )
    if (!project) {
      return Promise.resolve([null, null, 'Project not found in account'])
    }

    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`/accounts/${accountId}/projects/${projectId}`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: {
          name: project.name,
          moduleTags: tagIds.map(id => ({
            moduleTagDefinitionId: id,
            isTagged: true
          }))
        }
      })
    )
  }

  listAllAccountsAndProjectsAndModules = async () => {
    const [accounts, accStatus, accError] = await this.listAccounts()
    if (accError || !accounts) {
      return [null, accStatus, accError]
    }

    const projectResults = await Promise.all(
      accounts.map((account: { accountId: string }) =>
        this.listProjects(account.accountId)
      )
    )

    const projectError = projectResults.find(([, , error]) => !!error)
    if (projectError) {
      return projectError
    }

    const results = accounts.map((account: any, i: number) => {
      const [projects] = projectResults[i]

      return {
        account,
        projects
      }
    })

    return [results, 200, null]
  }

  //
  // Notifications

  listNotifications = async (accountId: string, projectId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/notifications`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getNotification = async (
    accountId: string,
    projectId: string,
    notificationId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/notifications/${notificationId}`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  updateNotification = async (
    accountId: string,
    projectId: string,
    notificationDefinitionId: string,
    modules: {
      label: string
      notificationKey: string
      template: string
      isDisabled: boolean
    }[]
  ) => {
    const updatedNotifications: any[] = []
    for (let i = 0; i < modules.length; i++) {
      const module: any = {
        label: modules[i].label,
        isDisabled: modules[i].isDisabled,
        notificationDefinitionId: notificationDefinitionId,
        notificationKey: modules[i].notificationKey,
        settings: {
          ConfiguredRecipients: [],
          Template: modules[i].template,
          ContentSubstitutions: []
        }
      }
      updatedNotifications.push(module)
    }

    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/notifications/${notificationDefinitionId}/`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: {
          updatedNotifications: updatedNotifications
        }
      })
    )
  }

  //
  // Connections
  getConnection = async (
    accountId: string,
    projectId: string,
    connectionId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/connections/${connectionId}`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  listConnections = async (accountId: string, projectId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/connections`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  registerConnection = async (
    accountId: string,
    projectId: string,
    {
      name,
      connUsername,
      connPassword,
      providerDefinitionId
    }: {
      name: string
      connUsername: string
      connPassword: string
      providerDefinitionId: string
    }
  ) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/connections/register`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 201 || status === 200,
        data: {
          name: name,
          providerDefinitionId: providerDefinitionId,
          incomingCredentials: [
            {
              userName: connUsername,
              password: connPassword,
              description: 'Credentials'
            }
          ],
          outgoingCredentials: []
        }
      })
    )
  }

  updateConnection = async (
    accountId: string,
    projectId: string,
    connectionId: string,
    {
      name,
      connUsername,
      connPassword,
      providerDefinitionId,
      isChanged
    }: {
      name: string
      connUsername: string
      connPassword: string
      providerDefinitionId: string
      isChanged: boolean
    }
  ) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/connections/${connectionId}`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: {
          name: name,
          providerDefinitionId: providerDefinitionId,
          incomingCredentials: [
            {
              userName: connUsername,
              password: connPassword,
              description: 'Credentials',
              isChanged: isChanged
            }
          ],
          outgoingCredentials: []
        }
      })
    )
  }

  updateConnectionInstruments = async (
    accountId,
    projectId,
    connectionId,
    instruments
  ) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/connections/${connectionId}/instruments`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: instruments
      })
    )
  }

  getConnectionInstruments = async (accountId, projectId, connectionId) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/connections/${connectionId}/instruments`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getAllConnectionInstruments = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/connections/all-email-instruments`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  //
  // Records

  getInTray = async ({
    startDateIso = null,
    endDateIso = null,
    accountIds = [],
    projectIds = [],
    statuses = [],
    modules = [],
    companyName = null,
    movementId = null,
    assignees = [],
    countries = [],
    templateNames = [],
    continuationToken = null
  } = {}) => {
    const headers = await this._getHeaders()
    this.addToRecordQueryHeaders(
      headers,
      accountIds,
      projectIds,
      assignees,
      continuationToken
    )
    const filterStatuses =
      statuses?.length > 0 ? statuses : API.DEFAULT_STATUS_OPTIONS
    try {
      const result = await wrapCall(
        axios({
          method: 'GET',
          url: this._getUri(`dashboard/records`),
          headers: headers,
          validateStatus: status => status === 200,
          signal: API.abortController.signal,
          params: {
            startDate: startDateIso,
            endDate: endDateIso,
            statuses: filterStatuses,
            modules: modules,
            companyName: companyName,
            movementId: movementId,
            countries: countries,
            templateNames: templateNames,
            pageSize: 300
          },
          paramsSerializer: params => {
            return stringify(params)
          }
        })
      )
      return result
    } catch (error) {
      return [null, 0, error]
    }
  }

  addToRecordQueryHeaders = (
    headers: Record<string, string>,
    accountIds = [],
    projectIds = [],
    assignees = [],
    continuationToken = null
  ) => {
    if (accountIds.length > 0) {
      headers['accountids'] = accountIds.join(',')
    }
    if (projectIds.length > 0) {
      headers['projectids'] = projectIds.join(',')
    }
    if (assignees.length > 0) {
      headers['assignees'] = assignees.join(',')
    }
    if (continuationToken) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      headers['continuationToken'] = continuationToken!
    }
  }

  getInTrayCount = async ({
    startDateIso = null,
    endDateIso = null,
    accountIds = [],
    projectIds = [],
    statuses = [],
    modules = [],
    companyName = null,
    movementId = null,
    assignees = [],
    countries = [],
    templateNames = []
  } = {}) => {
    const headers = await this._getHeaders()
    this.addToRecordQueryHeaders(
      headers,
      accountIds,
      projectIds,
      assignees,
      null
    )
    const filterStatuses =
      statuses?.length > 0 ? statuses : API.DEFAULT_STATUS_OPTIONS
    try {
      const result = await wrapCall(
        axios({
          method: 'GET',
          url: this._getUri(`dashboard/records-count`),
          headers: headers,
          validateStatus: status => status === 200,
          signal: API.abortController.signal,
          params: {
            startDate: startDateIso,
            endDate: endDateIso,
            statuses: filterStatuses,
            modules: modules,
            companyName: companyName,
            movementId: movementId,
            countries: countries,
            templateNames: templateNames,
            pageSize: 100
          },
          paramsSerializer: params => {
            return stringify(params)
          }
        })
      )
      return result
    } catch (error) {
      return [null, 0, error]
    }
  }

  getRecord = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName.toLowerCase()}/admin/records/${recordId}`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  transferRecordToModule = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string,
    destinationModuleName: string
  ) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName}/records/${recordId}/transfer`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: {
          destinationModuleName: destinationModuleName
        }
      })
    )
  }

  transferRecordToProject = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string,
    destinationProjectId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName.toLowerCase()}/admin/records/${recordId}/transfer-project`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: {
          destinationProjectId: destinationProjectId
        }
      })
    )
  }

  getRecordsFieldValues = async ({
    startDateIso = null,
    endDateIso = null,
    accountIds = [],
    projectIds = [],
    statuses = [],
    module = null,
    companyName = null,
    movementId = null,
    fieldName = null,
    hint = null
  } = {}) => {
    const headers = await this._getHeaders()
    this.addToRecordQueryHeaders(headers, accountIds, projectIds)
    const filterStatuses =
      statuses?.length > 0 ? statuses : API.DEFAULT_STATUS_OPTIONS
    try {
      const result = await wrapCall(
        axios({
          method: 'GET',
          url: this._getUri('dashboard/records-field-values'),
          headers: headers,
          validateStatus: status => status === 200,
          signal: API.abortController.signal,
          params: {
            startDate: startDateIso,
            endDate: endDateIso,
            statuses: filterStatuses,
            module: module,
            companyName: companyName,
            movementId: movementId,
            accountIds: accountIds,
            projectIds: projectIds,
            fieldName: fieldName,
            hint: hint
          },
          paramsSerializer: params => {
            return stringify(params)
          }
        })
      )
      return result
    } catch (error) {
      return [null, 0, error]
    }
  }

  setRecordsFieldValues = async (
    recordIdsByFieldValues: string[],
    moduleName: string,
    newValue: string,
    fieldPath: string
  ) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri('dashboard/mass-update-records-field-values'),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: {
          recordIdsByFieldValues: recordIdsByFieldValues,
          moduleName: moduleName,
          newValue: newValue,
          fieldPath: fieldPath
        }
      })
    )
  }

  revertRecordsFieldValues = async (
    logId: string,
    entries: {
      oldValue: string
      updatedValue: string
      currentValue: string
      index: number | null
      recordId: string
      projectId: string
    }[],
    fieldName: string,
    isChildField: boolean,
    moduleName: string,
    reversalStatus: string
  ) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri('dashboard/revert-mass-update-records'),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: {
          logId: logId,
          entries: entries,
          fieldName: fieldName,
          isChildField: isChildField,
          moduleName: moduleName,
          reversalStatus: reversalStatus
        }
      })
    )
  }

  getMassUpdateLogs = async () => {
    const headers = await this._getHeaders()
    this.addToRecordQueryHeaders(headers)

    try {
      const result = await wrapCall(
        axios({
          method: 'GET',
          url: this._getUri('dashboard/mass-update-logs'),
          headers: headers,
          validateStatus: status => status === 200,
          signal: API.abortController.signal
        })
      )
      return result
    } catch (error) {
      return [null, 0, error]
    }
  }

  getViewMassUpdateDiff = async (logId: string) => {
    const headers = await this._getHeaders()
    this.addToRecordQueryHeaders(headers)

    try {
      const result = await wrapCall(
        axios({
          method: 'GET',
          url: this._getUri('dashboard/view-mass-update-diff'),
          headers: headers,
          validateStatus: status => status === 200,
          signal: API.abortController.signal,
          params: {
            logId: logId
          },
          paramsSerializer: params => {
            return stringify(params)
          }
        })
      )
      return result
    } catch (error) {
      return [null, 0, error]
    }
  }

  getInfractions = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/infractions/modules/${moduleName}/records/${recordId}`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getPendingInfractions = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/infractions/modules/${moduleName}/records/${recordId}/pending`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  createManualInfraction = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string,
    infractions: {
      fieldName: string
      fieldNameAlias: string
      message: string
    }[] = []
  ) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/infractions/modules/${moduleName}/records/${recordId}/manual`
        ),
        data: infractions,
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status)
      })
    )
  }

  deletePendingInfraction = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string,
    field: string
  ) => {
    const encodedField = encodeURIComponent(field)
    return await wrapCall(
      axios({
        method: 'DELETE',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/infractions/modules/${moduleName}/records/${recordId}/pending/${encodedField}`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 204
      })
    )
  }

  updateRecord = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string,
    record: any,
    dataView: string | undefined
  ) => {
    const RAW = 'raw'
    const ENRICHED = 'enriched'
    const payload =
      dataView === RAW
        ? {
            raw: record.raw,
            status: record.status,
            internalNotes: record.internalNotes,
            assignee: record.assignee,
            eTag: record.eTag
          }
        : dataView === ENRICHED
          ? {
              enriched: record.enriched,
              status: record.status,
              internalNotes: record.internalNotes,
              assignee: record.assignee,
              eTag: record.eTag
            }
          : {
              // Send record.data as enriched
              enriched: record.data,
              status: record.status,
              internalNotes: record.internalNotes,
              assignee: record.assignee,
              eTag: record.eTag
            }

    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName.toLowerCase()}/admin/records/${recordId}${dataView === RAW ? '/raw' : ''}`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 201,
        data: payload
      })
    )
  }

  updateAssignee = async (assignee: string, records: []) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`/dashboard/assign`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: { assignee, records }
      })
    )
  }

  markAsDuplicate = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string,
    winningRecordProjectId: string,
    winningRecordModuleTagDefinition: string,
    winningRecordRecordId: string,
    reason: string
  ) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName.toLowerCase()}/admin/records/${recordId}/mark-as-duplicate`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: {
          winningRecordProjectId,
          winningRecordModuleTagDefinition,
          winningRecordRecordId,
          reason
        }
      })
    )
  }

  //GET RECORD HISTORY
  downloadRecordHistory = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName.toLowerCase()}/admin/records/${recordId}/historyspreadsheet`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        responseType: 'blob'
      })
    )
  }

  // Change Status of one or more records
  bulkStatusUpdate = async (newStatus: string, records: []) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri('/dashboard/mass-update-record-statuses'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: { newRecordStatus: newStatus, recordsToBeUpdated: records }
      })
    )
  }

  // Resend Records to QCR for reprocessing
  resendToQCR = async (records: [], targetTemplate: string | null) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri('/qcr/qcr-requeue'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: {
          requeuedRecords: records,
          targetTemplate: targetTemplate ? targetTemplate : null
        }
      })
    )
  }

  // ReferenceProductItems
  getReferenceProductItemCounts = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    hint: string
  ) => {
    let params = {}
    if (hint && hint !== '') {
      params = { hint: hint }
    }
    return await wrapCall(
      axios.get(
        this._getUri(
          `referencedata/accounts/${accountId}/projects/${projectId}/modules/${moduleName}/referenceproductcounts`
        ),
        {
          headers: await this._getHeaders(),
          params: params,
          validateStatus: status => status === 200
        }
      )
    )
  }

  /** @returns {Promise<[{items: [], hasNextPage: boolean, continuationToken: string}, any, any]>} */
  getReferenceProductItems = async ({
    searchTerm = null,
    pageSize = 100,
    continuationToken = null,
    sort = 'name',
    cancelToken = null
  }: Partial<{
    searchTerm: string | null
    pageSize: number
    continuationToken: string | null
    sort: string
    cancelToken: any
  }> = {}) => {
    const params: any = {
      'continuationToken': continuationToken,
      'page[size]': pageSize,
      'sort': sort
    }

    if (typeof searchTerm === 'string' && searchTerm?.length > 0) {
      params['search[name]'] = searchTerm
    }

    const [data, status, error] = await wrapCall(
      axios.get(this._getUri(`referencedata/referenceproductitems`), {
        headers: await this._getHeaders(),
        params,
        validateStatus: status => status === 200,
        cancelToken: cancelToken
      })
    )
    if (error) {
      return [null, status, error]
    }

    return [
      {
        items: data.referenceProductItems,
        hasNextPage: data.hasNextPage,
        continuationToken: data.continuationToken
      },
      status,
      null
    ]
  }

  getReferenceProductItem = async (referenceProductId: string) => {
    return await wrapCall(
      axios.get(
        this._getUri(
          `referencedata/referenceproductitems/${referenceProductId}`
        ),
        {
          headers: await this._getHeaders(),
          validateStatus: status => status === 200
        }
      )
    )
  }

  updateReferenceProductItem = async (referenceProductItem: {
    referenceProductId: string
  }) => {
    return await wrapCall(
      axios.put(
        this._getUri(
          `referencedata/referenceproductitems/${referenceProductItem.referenceProductId}`
        ),
        referenceProductItem,
        {
          headers: await this._getHeaders(),
          validateStatus: (status: number) => status === 201
        }
      )
    )
  }

  registerReferenceProductItem = async (referenceProductItem: any) => {
    return await wrapCall(
      axios.put(
        this._getUri(`referencedata/referenceproductitems/register`),
        referenceProductItem,
        {
          headers: await this._getHeaders(),
          validateStatus: (status: number) => status === 201
        }
      )
    )
  }

  getWasteCarriers = async ({
    expireAfter = null,
    searchTerm = null,
    pageSize = 100,
    continuationToken = null,
    cancelToken = null
  }: Partial<{
    expireAfter: string | null
    searchTerm: string | null
    pageSize: number
    continuationToken: string | null
    cancelToken: any
  }> = {}): Promise<WrappedResponse<PagedResponse<WasteCarrier>>> => {
    const params: any = {
      'page[size]': pageSize
    }

    if (typeof expireAfter === 'string' && expireAfter?.length > 0) {
      params['expiresAfter'] = expireAfter
    }

    if (typeof searchTerm === 'string' && searchTerm?.length > 0) {
      params['hint'] = searchTerm
    }

    const headers = await this._getHeaders()
    headers['contToken'] = continuationToken ?? ''

    const [data, status, error] = await wrapCall(
      axios.get<PagedResponse<WasteCarrier>>(
        this._getUri(`referencedata/waste/carriers`),
        {
          headers: headers,
          params,
          validateStatus: status => status === 200,
          cancelToken: cancelToken
        }
      )
    )
    if (data == null) {
      return [null, status, error]
    }

    return [data, status, null]
  }

  getWasteContainerTypes = async () => {
    return await wrapCall(
      axios.get(this._getUri(`referencedata/waste/container-types`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getWasteFacilities = async ({
    expireAfter = null,
    searchTerm = null,
    pageSize = 100,
    continuationToken = null,
    cancelToken = null
  }: Partial<{
    expireAfter: string | null
    searchTerm: string | null
    pageSize: number
    continuationToken: string | null
    cancelToken: any
  }> = {}): Promise<WrappedResponse<PagedResponse<WasteFacility>>> => {
    const params: any = {
      'page[size]': pageSize
    }

    if (typeof expireAfter === 'string' && expireAfter?.length > 0) {
      params['expiresAfter'] = expireAfter
    }

    if (typeof searchTerm === 'string' && searchTerm?.length > 0) {
      params['hint'] = searchTerm
    }

    const headers = await this._getHeaders()
    headers['contToken'] = continuationToken ?? ''

    const [data, status, error] = await wrapCall(
      axios.get<PagedResponse<WasteFacility>>(
        this._getUri(`referencedata/waste/facilities`),
        {
          headers: headers,
          params,
          validateStatus: status => status === 200,
          cancelToken: cancelToken
        }
      )
    )
    if (data == null) {
      return [null, status, error]
    }

    return [data, status, null]
  }

  //
  // Records Summary

  getRecordsSummary = async () => {
    return await wrapCall(
      axios.get(this._getUri('dashboard/records-summary'), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  //
  // Certificate

  getCertificates = async ({
    pageSize = 10000,
    sort = 'certificationscheme,certificatenumber,certificationrating'
  } = {}) => {
    let hasNextPage = true
    let pagedResults: any[] = []
    let continuationToken: string | null = null
    let lastStatus: number | null = null

    while (hasNextPage) {
      const [data, status, error] = await wrapCall(
        axios.get<any>(this._getUri(`referencedata/certificates`), {
          headers: await this._getHeaders(),
          params: {
            'continuationToken': continuationToken,
            'page[size]': pageSize,
            'sort': sort
          },
          validateStatus: status => status === 200
        })
      )
      if (error) {
        return [null, status, error]
      }
      pagedResults = [...pagedResults, ...data.certificates]
      hasNextPage = data.hasNextPage
      continuationToken = data.continuationToken as string
      lastStatus = status
    }

    return [pagedResults, lastStatus, null]
  }

  getCertificate = async (certificateId: string) => {
    return await wrapCall(
      axios.get(this._getUri(`referencedata/certificates/${certificateId}`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  updateCertificate = async (certificate: { certificateId: string }) => {
    return await wrapCall(
      axios.put(
        this._getUri(`referencedata/certificates/${certificate.certificateId}`),
        certificate,
        {
          headers: await this._getHeaders(),
          validateStatus: (status: number) => status === 201
        }
      )
    )
  }

  registerCertificate = async (certificate: any) => {
    return await wrapCall(
      axios.put(
        this._getUri(`referencedata/certificates/register`),
        certificate,
        {
          headers: await this._getHeaders(),
          validateStatus: (status: number) => status === 201
        }
      )
    )
  }

  getUploadCertificateLink = async () => {
    return await wrapCall(
      axios.get(this._getUri(`referencedata/certificates/write-link`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getUploadCompaniesLink = async () => {
    return await wrapCall(
      axios.get(this._getUri(`referencedata/companies/write-link`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  /** @param {'certificates'|'wasteCarriers'} referenceDataType */
  queueReferenceDataImport = async (referenceDataType: string) => {
    return await wrapCall(
      axios.post(
        this._getUri(`referencedata/queueImportReferenceData`),
        { referenceDataType: referenceDataType },
        {
          headers: await this._getHeaders(),
          validateStatus: (status: number) => status === 200
        }
      )
    )
  }

  // Companies

  getCompanies = async ({ pageSize = 10000, sort = 'name' } = {}) => {
    let hasNextPage = true
    let pagedResults: any[] = []
    let continuationToken: string | null = null
    let lastStatus: number | null = null

    while (hasNextPage) {
      const headers = await this._getHeaders()
      const [data, status, error] = await wrapCall(
        axios.get(this._getUri(`referencedata/companies`), {
          headers: headers,
          params: {
            'page[size]': pageSize,
            'sort': sort,
            'continuationToken': continuationToken
          },
          validateStatus: status => status === 200
        })
      )
      if (error) {
        return [null, status, error]
      }
      pagedResults = [...pagedResults, ...data.companies]
      hasNextPage = data.hasNextPage
      continuationToken = data.continuationToken as string
      lastStatus = status
    }

    return [pagedResults, lastStatus, null]
  }

  getCompany = async (companyId: string) => {
    return await wrapCall(
      axios.get(this._getUri(`referencedata/companies/${companyId}`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  registerCompany = async (company: any) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(`referencedata/companies/register`),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: company
      })
    )
  }

  updateCompany = async (company: { companyId: string }) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`referencedata/companies/${company.companyId}`),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: company
      })
    )
  }

  mergeCompanies = async (targetCompanyId: string, otherCompanyIds = []) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`referencedata/companies/merge`),
        data: {
          companyId: targetCompanyId,
          otherCompanyIds: otherCompanyIds
        },
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  deleteCompany = async (companyId: string) => {
    return await wrapCall(
      axios.delete(this._getUri(`referencedata/companies/${companyId}`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  // Get Suppliers and Manufacturers
  getSuppliersAndManufacturers = async params => {
    return await wrapCall(
      axios.get(this._getUri(`referencedata/suppliersandmanufacturers`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        signal: API.abortController.signal,
        params: {
          ...params
        },
        paramsSerializer: params => {
          return stringify(params)
        }
      })
    )
  }

  // Suppliers

  getSupplier = async (companyId: string) => {
    return await wrapCall(
      axios.get(this._getUri(`referencedata/supplier/${companyId}`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  registerSupplier = async (company: any) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(`referencedata/supplier/register`),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: company
      })
    )
  }

  updateSupplier = async (company: { id: string }) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`referencedata/supplier/${company.id}`),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: company
      })
    )
  }

  deleteSupplier = async (companyId: string) => {
    return await wrapCall(
      axios.delete(this._getUri(`referencedata/supplier/${companyId}`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 204
      })
    )
  }

  // Manufacturers

  getManufacturer = async (companyId: string) => {
    return await wrapCall(
      axios.get(this._getUri(`referencedata/manufacturer/${companyId}`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  registerManufacturer = async (company: any) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(`referencedata/manufacturer/register`),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: company
      })
    )
  }

  updateManufacturer = async (company: { id: string }) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`referencedata/manufacturer/${company.id}`),
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status),
        data: company
      })
    )
  }

  deleteManufacturer = async (companyId: string) => {
    return await wrapCall(
      axios.delete(this._getUri(`referencedata/manufacturer/${companyId}`), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 204
      })
    )
  }

  // In Tray Filter Values
  getTemplateNames = async () => {
    return await wrapCall(
      axios.get(this._getUri('/dashboard/templates'), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  // Api Keys

  listApiKeys = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/public-api-keys`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getApiKey = async (id: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/public-api-keys/${id}`),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  updateApiKey = async (id: string, data: any) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`/public-api-keys/${id}`),
        data: data,
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status)
      })
    )
  }

  registerApiKey = async (data: any) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(`/public-api-keys/register`),
        data: data,
        headers: await this._getHeaders(),
        validateStatus: status => [200, 201].includes(status)
      })
    )
  }

  // Presentation

  autocomplete = async (
    {
      projectId,
      moduleName,
      fieldName,
      alias,
      hint
    }: {
      projectId: string
      moduleName: string
      fieldName: string
      alias: string | null
      hint: string | null
    },
    cancelToken = undefined
  ) => {
    return await wrapCall(
      axios.get(this._getUri(`/presentation/records/autocomplete`), {
        params: { projectId, moduleName, fieldName: alias || fieldName, hint },
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        cancelToken: cancelToken
      })
    )
  }

  getTimezones = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/presentation/timezones'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  // Users

  listSSOIDPs = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/users/ssoidps'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  listUsers = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/users'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  listUserAccountMappings = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/users/account-mappings'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  listQflowUsers = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/users/domains/qualisflow'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getUser = async (userId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/users/${userId}`),
        headers: await this._getHeaders(),
        validateStatus: status => [200].includes(status)
      })
    )
  }

  getUserAccountMappings = async (userId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/users/${userId}/account-mappings`),
        headers: await this._getHeaders(),
        validateStatus: status => [200].includes(status)
      })
    )
  }

  getUserSelf = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/users/self`),
        headers: await this._getHeaders(),
        validateStatus: status => [200].includes(status)
      })
    )
  }

  getUserSelfAccountMappings = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(`/users/self-account-mappings`),
        headers: await this._getHeaders(),
        validateStatus: status => [200].includes(status)
      })
    )
  }

  updateUser = async (user: { userId: string }) => {
    return await wrapCall(
      axios({
        method: 'PUT',
        url: this._getUri(`/users/${user.userId}`),
        headers: await this._getHeaders(),
        validateStatus: status => [200].includes(status),
        data: user
      })
    )
  }

  registerUser = async (user: any) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(`/users/register`),
        headers: await this._getHeaders(),
        validateStatus: status => [201, 204].includes(status),
        data: user
      })
    )
  }

  resetUserPassword = async (userId: string) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(`/users/${userId}/reset-password`),
        headers: await this._getHeaders(),
        validateStatus: status => [204].includes(status)
      })
    )
  }

  // OCR

  ocrRotate = async (file: File) => {
    if (!file) {
      return [null, false, null]
    }

    const headers = await this._getHeaders()
    headers['Content-Type'] = 'multipart/form-data'

    const data = new FormData()
    data.append('file', file)

    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri('/qcr/ocr-rotate'),
        headers: headers,
        data: data,
        validateStatus: status => [204, 200].includes(status)
      })
    )
  }

  getOcr = async (
    accountId: string,
    projectId: string,
    recordId: string,
    attachmentId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `qcr/ocr/accounts/${accountId}/projects/${projectId}/records/${recordId}/attachments/${attachmentId}`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status == 200
      })
    )
  }

  // SmartWaste

  getSmartWasteConfig = async (accountId: string, projectId: string) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/smartwasteconfiguration`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200 || status === 404
      })
    )
  }

  updateSmartWasteConfig = async (
    accountId: string,
    projectId: string,
    swConfig: any
  ) => {
    return await wrapCall(
      axios.put(
        this._getUri(
          `/accounts/${accountId}/projects/${projectId}/smartwasteconfiguration`
        ),
        swConfig,
        {
          headers: await this._getHeaders(),
          validateStatus: (status: number) => status === 200
        }
      )
    )
  }

  registerSmartWasteConfig = async (
    accountId: string,
    projectId: string,
    swConfig: any
  ) => {
    return await wrapCall(
      axios.post(
        this._getUri(
          `/accounts/${accountId}/projects/${projectId}/smartwasteconfiguration`
        ),
        swConfig,
        {
          headers: await this._getHeaders(),
          validateStatus: (status: number) => status === 201
        }
      )
    )
  }

  // BI Reports

  listBIReports = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/bireports/reports'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  biReportsWorkspaces = async () => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/bireports/workspaces'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  biReportsWorkspaceReports = async workspaceId => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri('/bireports/workspaces/' + workspaceId + '/reports'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  biReportsUserPermissions = async accountId => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          '/bireports/accounts/' + accountId + '/user-permissions'
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  upsertAccountBIReports = async (accountId, biReports) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri('/bireports/accounts/' + accountId + '/reports'),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: biReports
      })
    )
  }

  upsertAccountBIReportsUserPermissions = async (
    accountId,
    biReportUserPermissions
  ) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(
          '/bireports/accounts/' + accountId + '/user-permissions'
        ),
        headers: await this._getHeaders(),
        validateStatus: status => status === 200,
        data: biReportUserPermissions
      })
    )
  }

  postSplitRecord = async (
    accountId: string,
    moduleName: string,
    projectId: string,
    recordId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'POST',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName}/admin/records/${recordId}/split-record`
        ),
        headers: await this._getHeaders(),
        validateStatus: status => [204, 200].includes(status)
      })
    )
  }

  //
  // Addresses

  getAddresses = async (
    address: string,
    accountId: string,
    projectId: string
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/projectAddresses`
        ),
        headers: await this._getHeaders(),
        params: {
          searchAddress: address
        },
        validateStatus: status => status === 200
      })
    )
  }

  getKnownAddressSuggestions = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string,
    addressType:
      | 'deliveryAddress'
      | 'dispatchAddress'
      | 'originSiteAddress'
      | 'facilitySiteAddress'
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName}/admin/records/${recordId}/knownAddressSuggestions`
        ),
        headers: await this._getHeaders(),
        params: {
          addressType: addressType
        },
        validateStatus: status => status === 200
      })
    )
  }

  getClientKnownAddressSuggestions = async (
    accountId: string,
    projectId: string,
    moduleName: string,
    recordId: string,
    addressType:
      | 'deliveryAddress'
      | 'dispatchAddress'
      | 'originSiteAddress'
      | 'facilitySiteAddress'
  ) => {
    return await wrapCall(
      axios({
        method: 'GET',
        url: this._getUri(
          `/accounts/${accountId}/projects/${projectId}/modules/${moduleName}/records/${recordId}/knownAddressSuggestions`
        ),
        headers: await this._getHeaders(),
        params: {
          addressType: addressType
        },
        validateStatus: status => status === 200
      })
    )
  }

  //
  // Procore

  getProcoreCompanies = async () => {
    return await wrapCall(
      axios.get<any[]>(this._getUri('/procore/companies'), {
        headers: await this._getHeaders(),
        validateStatus: status => status === 200
      })
    )
  }

  getProcoreProjects = async (procoreCompanyId: string) => {
    return await wrapCall(
      axios.get<any[]>(
        this._getUri(`/procore/companies/${procoreCompanyId}/projects`),
        {
          headers: await this._getHeaders(),
          validateStatus: status => status === 200
        }
      )
    )
  }
}
